import ApiClient from 'api-client/ApiClient'
import DataKeys, { entityNamesByKey } from 'k8s/DataKeys'
import ActionsSet from 'core/actions/ActionsSet'
import ListAction from 'core/actions/ListAction'
import DeleteAction from 'core/actions/DeleteAction'
import UpdateAction from 'core/actions/UpdateAction'
import CustomAction from 'core/actions/CustomAction'
import CreateAction from 'core/actions/CreateAction'
import store from 'app/store'
import { cacheActions } from 'core/caching/cacheReducers'
import { noop } from 'utils/fp'
import { isAdmin } from 'app/plugins/infrastructure/components/common/helpers'

const { dispatch } = store
const { cinder, nova } = ApiClient.getInstance()

export const volumeTypeActions = ActionsSet.make<DataKeys.VolumeTypes>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.VolumeTypes,
  cacheKey: DataKeys.VolumeTypes,
})

export const listVolumeTypes = volumeTypeActions.add(
  new ListAction<DataKeys.VolumeTypes>(async () => {
    // Can't query both public and private volume types in one query...
    // Admins should be able to see private volume types, bc it's possible
    // that a volume type is private and has no tenancy, in which
    // case that volume type will never be visible, so must
    // query both
    if (isAdmin()) {
      const privateVolumeTypes = await cinder.getPrivateVolumeTypes()
      const volumeTypes = await cinder.getVolumeTypes()
      const privateVolumeTypesInOtherTenants = privateVolumeTypes.filter(
        (privateVolumeType) =>
          !volumeTypes.some((volumeType) => volumeType.id === privateVolumeType.id),
      )
      return [...volumeTypes, ...privateVolumeTypesInOtherTenants]
    } else {
      const volumeTypes = await cinder.getVolumeTypes()
      return volumeTypes
    }
  }),
)

export const createVolumeType = volumeTypeActions.add(
  new CreateAction<DataKeys.VolumeTypes, { body }>(async ({ body }) => {
    const created = await cinder.createVolumeType(body)
    const volumeTypeId = created.id
    const newVolumeType = await cinder.getVolumeType(volumeTypeId)
    return newVolumeType
  }),
)

export const updateVolumeType = volumeTypeActions.add(
  new UpdateAction<
    DataKeys.VolumeTypes,
    {
      id: string
      body: unknown
      keysToDelete: string[]
    }
  >(async ({ id, body, keysToDelete }) => {
    await cinder.updateVolumeType(id, body, keysToDelete)
    const updatedVolumeType = await cinder.getVolumeType(id)
    return updatedVolumeType
  }),
)

export const deleteVolumeType = volumeTypeActions.add(
  new DeleteAction<DataKeys.VolumeTypes, { id: string }>(async ({ id }) => {
    await cinder.deleteVolumeType(id)
  }),
)

export const addVolumeTypeTenant = volumeTypeActions.add(
  new CustomAction<DataKeys.VolumeTypes, { id: string; body?: any }>(
    'addVolumeTypeTenant',
    async ({ id, body }) => {
      return cinder.addVolumeTypeTenant({ id, body })
    },
    (result, { id }) => {
      noop()
    },
  ),
)

export const removeVolumeTypeTenant = volumeTypeActions.add(
  new CustomAction<DataKeys.VolumeTypes, { id: string; body?: any }>(
    'removeVolumeTypeTenant',
    async ({ id, body }) => {
      return cinder.removeVolumeTypeTenant({ id, body })
    },
    (result, { id }) => {
      noop()
    },
  ),
)

export const getVolumeTypeTenants = async ({ id }) => {
  return cinder.getVolumeTypeTenants({ id })
}

export const tenantDefaultVolumeTypeActions = ActionsSet.make<DataKeys.TenantDefaultVolumeTypes>({
  uniqueIdentifier: 'id',
  entityName: entityNamesByKey.TenantDefaultVolumeTypes,
  cacheKey: DataKeys.TenantDefaultVolumeTypes,
})

export const listTenantDefaultVolumeType = tenantDefaultVolumeTypeActions.add(
  new ListAction<DataKeys.TenantDefaultVolumeTypes>(async () => {
    return cinder.getDefaultTenantVolumeType()
  }),
)

export const setDefaultVolumeTypeTenant = tenantDefaultVolumeTypeActions.add(
  new UpdateAction<DataKeys.TenantDefaultVolumeTypes, { id: string, body?: any }>(
    async ({ id, body }) => {
      return cinder.setDefaultVolumeType(id, body)
    },
  ),
)

